import React, { useEffect, useState } from "react";
import { InteractionType, EventType } from "@azure/msal-browser";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  MsalProvider,
  UnauthenticatedTemplate,
  MsalAuthenticationTemplate,
} from "@azure/msal-react";
import ProtectedRoutes from "./Generic/ProtectedRoutes";
import Authenticating from "./Generic/Authenticating";
import Nav from "./components/navigation/Nav.js";
import Dashboard from "./components/views/Dashboard/Dashboard.js";
import { makeStyles } from "@material-ui/core/styles";

//Action
import EditNumberStatus from "./components/views/action/EditNumberStatus/EditNumberStatus.js";
import ManageTeamsEndpoint from "./components/views/action/ManageTeamsEndpoint/ManageTeamsEndpoint";
import ManageQueueStatus from "./components/views/action/ManageQueStatus/ManageQueueStatus";
import EditQueue from "./components/views/action/EditQueueStatus";

//Operations
import TeamsEndpointAudit from "./components/views/operations/TeamsEndpointAudit/TeamsEndpointAudit";
import IPTEndpointAudit from "./components/views/operations/IPTEndpointAudit/IPTEndpointAudit";
import SiteAudit from "./components/views/operations/SiteAudit/SiteAudit";
import PoolAudit from "./components/views/operations/PoolAudit/PoolAudit";
import RangeAudit from "./components/views/operations/RangeAudit/RangeAudit";
import TestingNumbers from "./components/views/operations/TestingNumbers/TestingNumbers";
import OffboardedUsersAudit from "./components/views/operations/OffBoardedUserAudit/OffboardedUsersAudit";

//Administration
import Site from "./components/views/administration/Sites/Site";
import EditSite from "./components/views/administration/Sites/EditSite";
import EditSecondarySite from "./components/views/administration/Sites/EditSecondarySite";
import Pool from "./components/views/administration/Pools/Pool";
import EditPool from "./components/views/administration/Pools/EditPool";
import Range from "./components/views/administration/Ranges/Range";
import EditRange from "./components/views/administration/Ranges/EditRange";
import RangesByPool from "./components/views/administration/Ranges/RangesByPool";
import PoolsBySite from "./components/views/administration/Pools/PoolsBySite";
import UpdateNumbers from "./components/views/administration/Ranges/UpdateRangeNumbers";
import PhoneNumbersByRange from "./components/views/administration/PhoneNumbers/PhoneNumbersByRange";
import Carrier from "./components/views/administration/Carrier/Carrier";
import EditCarrier from "./components/views/administration/Carrier/EditCarrier";
import AdminMailingList from "./components/views/administration/AdminMailingList/AdminMailingList ";
import ReviewOrderHistory from "./components/views/administration/ReviewOrderHistory/ReviewOrderHistory";
import SyncFromTeams from "./components/views/administration/SyncFromTeams/SyncFromTeams";
import RunSiteDataSync from "./components/views/administration/RunSiteDataSync/RunSiteDataSync";
import DevPortal from "./components/views/DevPortal.js";

import SearchResult from "./components/views/Search/SearchResults.js";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(8),
    marginLeft: drawerWidth,
    marginTop: "48px",
  },
  loading: {
    marginTop: "25%",
    marginLeft: "50%",
  },
}));

function App({ msalInstance }) {
  const classes = useStyles();
  const [isAdmin, setIsAdmin] = useState(false);
  const [authObj, setAuthObj] = useState({ roles: null });

  useEffect(() => {
    setIsAdmin(
      msalInstance?.getActiveAccount()?.idTokenClaims?.roles?.includes("admin")
    );
    setAuthObj({
      roles: msalInstance?.getActiveAccount()?.idTokenClaims?.roles,
    });
  }, [msalInstance]);

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      setIsAdmin(account?.idTokenClaims?.roles?.includes("admin"));
      setAuthObj({ roles: account?.idTokenClaims?.roles });
    }
  });

  return (
    <BrowserRouter>
      <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
          <ProtectedRoutes msalInstance={msalInstance}>
            <Nav msalInstance={msalInstance} />
            <div className={classes.content}>
              <Routes>
                <Route path="/" element={<Dashboard isAdmin={isAdmin} />} />
                <Route
                  path="/editNumberStatus"
                  element={<EditNumberStatus />}
                />
                <Route
                  path="ManageTeamsEndpoint/*"
                  element={<ManageTeamsEndpoint />}
                  isAdmin={isAdmin}
                />
                <Route
                  path="/manageQueueStatus"
                  element={<ManageQueueStatus />}
                />
                {/* Operations */}
                <Route
                  path="/endpointAuditTeams"
                  element={<TeamsEndpointAudit />}
                />
                <Route
                  element={<IPTEndpointAudit />}
                  path="/endpointAuditIPT"
                />
                <Route element={<SiteAudit />} path="/siteAudit" />
                <Route element={<PoolAudit />} path="/poolAudit" />
                <Route element={<RangeAudit />} path="/rangeAudit" />
                <Route element={<TestingNumbers />} path="/testingNumbers" />
                <Route
                  element={<OffboardedUsersAudit />}
                  path="/offboardedUsersAudit"
                />
                {/* Operations */}
                <Route
                  element={<TeamsEndpointAudit />}
                  path="/endpointAuditTeams"
                />
                <Route
                  element={<IPTEndpointAudit />}
                  path="/endpointAuditIPT"
                />
                <Route element={<SiteAudit />} path="/siteAudit" />
                <Route element={<PoolAudit />} path="/poolAudit" />
                <Route element={<RangeAudit />} path="/rangeAudit" />
                <Route element={<TestingNumbers />} path="/testingNumbers" />
                <Route
                  element={<OffboardedUsersAudit />}
                  path="/offboardedUsersAudit"
                />
                {/* Administration */}
                <Route path="site/*" element={<Site isAdmin={isAdmin} />} />

                <Route element={<EditSite />} path="/sites/edit/:siteId" />
                <Route
                  element={<EditSecondarySite />}
                  path="/secondarySites/edit/:siteId"
                />
                <Route path="pool/*" element={<Pool isAdmin={isAdmin} />} />
                <Route element={<EditPool />} path="/pools/edit/:poolId" />
                <Route
                  element={<PoolsBySite isAdmin={isAdmin} />}
                  path="/pools/bySite/:siteId"
                />
                <Route path="range/*" element={<Range isAdmin={isAdmin} />} />
                <Route element={<EditRange />} path="/ranges/edit/:rangeId" />
                <Route
                  element={<RangesByPool />}
                  path="/ranges/byPool/:poolId"
                />
                <Route
                  element={<UpdateNumbers />}
                  path="/ranges/updateNumbers/:rangeId"
                />
                <Route
                  element={<PhoneNumbersByRange />}
                  path="/phoneNumbers/byRange/:rangeId"
                />
                <Route
                  path="carrier/*"
                  element={<Carrier isAdmin={isAdmin} />}
                />

                <Route
                  element={<EditCarrier />}
                  path="/carriers/edit/:carrierId"
                />
                {/* <Route element={<UserPermissions />} path="/userPermissions" /> */}
                <Route
                  element={<AdminMailingList />}
                  path="/adminMailingList"
                />
                <Route
                  element={<ReviewOrderHistory />}
                  path="/reviewOrderHistory"
                />
                <Route element={<SyncFromTeams />} path="/syncfromTeams" />
                <Route element={<RunSiteDataSync />} path="/runSiteDataSync" />
                <Route element={<DevPortal />} path="/devPortal" />

                <Route
                  element={<SearchResult authObj={authObj} />}
                  path="/search/:searchBy/:searchVal"
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </div>
          </ProtectedRoutes>
        </MsalAuthenticationTemplate>
        <UnauthenticatedTemplate>
          <Authenticating />
        </UnauthenticatedTemplate>
      </MsalProvider>
    </BrowserRouter>
  );
}

export default App;
